import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import LaufProtokoll from './LaufProtokoll';
import {
  prozessLayout,
  BAHN_HOEHE,
  SPALTE_BREITE,
  ProzessSchritt,
  LaufSchritt,
  LayoutKnoten,
} from '../lib/prozessLayout';
import './ProzessAnsicht.css';

export type { ProzessSchritt, LaufSchritt };

export interface ProzessAnsichtProzess {
  key: string;
  name: string;
  schritte: ProzessSchritt[];
  statuskette: string[];
}

export interface ProzessAnsichtLauf {
  schritte: LaufSchritt[];
  status: string;
}

/** Ein Lauf aus GET /api/prozess-laeufe (Auszug der genutzten Felder). */
export interface ProzessLauf extends ProzessAnsichtLauf {
  lauf_id: number;
  prozess_key: string;
  mitarbeiter_name: string | null;
  ausloeser_typ: string;
  entitaet_typ: string | null;
  entitaet_id: number | null;
  konversation_id: number | null;
  abbruch_grund: string | null;
  gestartet_am: string | null;
  beendet_am: string | null;
  dauer_s: number | null;
}

interface Props {
  prozess: ProzessAnsichtProzess;
  agentName?: string;
  menschName?: string;
  lauf?: ProzessAnsichtLauf | null;
  /** Mit Token lädt die Ansicht die Läufe des Prozesses und zeigt sie unter dem Diagramm. */
  authToken?: string;
}

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const STATUS_TEXT: Record<string, string> = {
  LAEUFT: 'läuft',
  WARTET_FREIGABE: 'wartet auf Freigabe',
  ABGESCHLOSSEN: 'abgeschlossen',
  ABGEBROCHEN: 'abgebrochen',
  FEHLER: 'Fehler',
};

const AUSLOESER_TEXT: Record<string, string> = {
  ereignis: 'Ereignis',
  zeitplan: 'Zeitplan',
  email: 'E-Mail',
  manuell: 'Manuell',
};

function dauerText(s: number | null): string {
  if (s == null) return '–';
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
}

const KNOTEN_BREITE = 130;
const KNOTEN_HOEHE = 56;
const RAUTE_HALB_B = 52;
const RAUTE_HALB_H = 34;
const KREIS_R = 16;
const MITTE_Y = 58; // vertikale Mitte der Knoten innerhalb einer Bahn (Platz für Bahn-Label oben)

/** Bricht Text in höchstens `maxZeilen` Zeilen mit je ~`breite` Zeichen. */
function umbrechen(text: string, breite: number, maxZeilen: number): string[] {
  const woerter = text.split(/\s+/).filter(Boolean);
  const zeilen: string[] = [];
  let aktuell = '';
  for (const w of woerter) {
    if (aktuell && (aktuell + ' ' + w).length > breite) {
      zeilen.push(aktuell);
      aktuell = w;
    } else {
      aktuell = aktuell ? aktuell + ' ' + w : w;
    }
  }
  if (aktuell) zeilen.push(aktuell);
  if (zeilen.length > maxZeilen) {
    const rest = zeilen.slice(0, maxZeilen);
    rest[maxZeilen - 1] = rest[maxZeilen - 1].slice(0, Math.max(0, breite - 1)) + '…';
    return rest;
  }
  return zeilen;
}

function halbeBreite(form: LayoutKnoten['form']): number {
  if (form === 'start' || form === 'ende') return KREIS_R;
  if (form === 'gateway') return RAUTE_HALB_B;
  return KNOTEN_BREITE / 2;
}

const ProzessAnsicht: React.FC<Props> = ({ prozess, agentName, menschName, lauf: laufProp, authToken }) => {
  const { schritte, statuskette } = prozess;
  const [laeufe, setLaeufe] = useState<ProzessLauf[]>([]);
  const [laeufeFehler, setLaeufeFehler] = useState('');
  const [gewaehlt, setGewaehlt] = useState<number | null>(null);
  const [protokollLauf, setProtokollLauf] = useState<number | null>(null);

  const ladeLaeufe = useCallback(async () => {
    if (!authToken) return;
    setLaeufeFehler('');
    try {
      const res = await axios.get(`${API_URL}/api/prozess-laeufe`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { prozess_key: prozess.key, limit: 20 },
      });
      setLaeufe(res.data);
    } catch {
      setLaeufeFehler('Läufe konnten nicht geladen werden.');
    }
  }, [authToken, prozess.key]);

  useEffect(() => {
    setGewaehlt(null);
    setProtokollLauf(null);
    ladeLaeufe();
  }, [ladeLaeufe]);

  const abbrechen = async (l: ProzessLauf) => {
    const grund = window.prompt('Grund für den Abbruch:');
    if (grund === null) return;
    try {
      await axios.post(
        `${API_URL}/api/prozess-laeufe/${l.lauf_id}/abbrechen`,
        { grund: grund.trim() || 'Manuell abgebrochen' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      ladeLaeufe();
    } catch (e: any) {
      setLaeufeFehler(e?.response?.data?.detail || 'Lauf konnte nicht abgebrochen werden.');
    }
  };

  const lauf: ProzessAnsichtLauf | null =
    laufProp ?? laeufe.find(l => l.lauf_id === gewaehlt) ?? null;
  const layout = prozessLayout(schritte);
  const hoehe = BAHN_HOEHE * 3;
  const schrittByKey = Object.fromEntries(schritte.map(s => [s.key, s]));
  const laufByKey: Record<string, LaufSchritt> = lauf
    ? Object.fromEntries(lauf.schritte.map(l => [l.key, l]))
    : {};
  const knotenByKey = Object.fromEntries(layout.knoten.map(k => [k.key, k]));

  const bahnen: { bahn: 'ausloeser' | 'agent' | 'mensch'; label: string }[] = [
    { bahn: 'ausloeser', label: 'Auslöser' },
    { bahn: 'agent', label: agentName ?? 'Agent' },
    { bahn: 'mensch', label: menschName ?? 'Mensch' },
  ];

  const statusKlasse = (k: LayoutKnoten): string => {
    if (!lauf) return '';
    if (k.key === '__start') return 'pz-knoten--erledigt';
    if (k.key === '__ende') {
      if (lauf.status === 'ABGESCHLOSSEN') return 'pz-knoten--erledigt';
      if (lauf.status === 'FEHLER' || lauf.status === 'ABGEBROCHEN') return 'pz-knoten--fehler';
      return '';
    }
    const l = laufByKey[k.key];
    return l ? `pz-knoten--${l.status}` : '';
  };

  const tooltip = (k: LayoutKnoten): string => {
    const s = schrittByKey[k.key];
    if (!s) return k.key === '__start' ? 'Start' : 'Ende';
    const teile = [s.was];
    if (s.entscheidung) teile.push(`Entscheidung: ${s.entscheidung}`);
    if (s.delegation) teile.push(`Delegation: ${s.delegation}`);
    if (s.tools.length) teile.push(`Tools: ${s.tools.join(', ')}`);
    if (s.uebergabe) teile.push('Übergabe an Mensch');
    const l = laufByKey[k.key];
    if (l) {
      teile.push(`Status: ${l.status}${l.zeit ? ` (${l.zeit})` : ''}`);
      if (l.detail) teile.push(l.detail);
    }
    return teile.join('\n');
  };

  const kantenPfad = (vonKey: string, nachKey: string): string => {
    const v = knotenByKey[vonKey];
    const n = knotenByKey[nachKey];
    const x1 = v.x + SPALTE_BREITE / 2 + halbeBreite(v.form);
    const y1 = v.y + MITTE_Y;
    const x2 = n.x + SPALTE_BREITE / 2 - halbeBreite(n.form);
    const y2 = n.y + MITTE_Y;
    if (y1 === y2) return `M ${x1} ${y1} H ${x2}`;
    const mitte = (x1 + x2) / 2;
    return `M ${x1} ${y1} H ${mitte} V ${y2} H ${x2}`;
  };

  const renderKnoten = (k: LayoutKnoten) => {
    const cx = k.x + SPALTE_BREITE / 2;
    const cy = k.y + MITTE_Y;
    const klasse = statusKlasse(k);
    const s = schrittByKey[k.key];
    let form: React.ReactNode;
    let beschriftung: string[] = [];

    if (k.form === 'start' || k.form === 'ende') {
      form = (
        <circle cx={cx} cy={cy} r={KREIS_R} className={`pz-knoten pz-knoten--${k.form} ${klasse}`} />
      );
    } else if (k.form === 'gateway') {
      const pts = `${cx},${cy - RAUTE_HALB_H} ${cx + RAUTE_HALB_B},${cy} ${cx},${cy + RAUTE_HALB_H} ${cx - RAUTE_HALB_B},${cy}`;
      form = <polygon points={pts} className={`pz-knoten ${klasse}`} />;
      beschriftung = umbrechen(s?.entscheidung ?? '', 14, 3);
    } else {
      form = (
        <rect
          x={cx - KNOTEN_BREITE / 2}
          y={cy - KNOTEN_HOEHE / 2}
          width={KNOTEN_BREITE}
          height={KNOTEN_HOEHE}
          className={`pz-knoten ${k.form === 'mensch' ? 'pz-knoten--mensch' : ''} ${klasse}`}
        />
      );
      beschriftung = umbrechen(s?.was ?? '', 20, 3);
    }

    const startY = cy - ((beschriftung.length - 1) * 12) / 2 + 4;
    return (
      <g key={k.key} data-knoten={k.key}>
        <title>{tooltip(k)}</title>
        {form}
        {beschriftung.map((z, i) => (
          <text key={i} x={cx} y={startY + i * 12} className="pz-text">
            {z}
          </text>
        ))}
        {(k.form === 'start' || k.form === 'ende') && (
          <text x={cx} y={cy + KREIS_R + 14} className="pz-text pz-text--klein">
            {k.form === 'start' ? 'Start' : 'Ende'}
          </text>
        )}
        {s?.uebergabe && (
          <text x={cx} y={cy + KNOTEN_HOEHE / 2 + 12} className="pz-text pz-text--klein">
            Übergabe
          </text>
        )}
      </g>
    );
  };

  const markerId = `pz-pfeil-${prozess.key}`;

  return (
    <div className="pz-ansicht">
      <div className="pz-scroll">
        <svg
          className="pz-svg"
          viewBox={`0 0 ${layout.breite} ${hoehe}`}
          width={layout.breite}
          height={hoehe}
          role="img"
          aria-label={`Prozessansicht ${prozess.name}`}
        >
          <defs>
            <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="pz-pfeil" />
            </marker>
          </defs>
          {bahnen.map((b, i) => (
            <g key={b.bahn}>
              <rect x={0} y={i * BAHN_HOEHE} width={layout.breite} height={BAHN_HOEHE} className={`pz-bahn--${b.bahn}`} />
              <line x1={0} x2={layout.breite} y1={i * BAHN_HOEHE} y2={i * BAHN_HOEHE} className="pz-bahn-linie" />
              <text x={8} y={i * BAHN_HOEHE + 16} className="pz-bahn-label">
                {b.label}
              </text>
            </g>
          ))}
          {layout.kanten.map(e => (
            <path
              key={`${e.von}-${e.nach}`}
              d={kantenPfad(e.von, e.nach)}
              className="pz-kante"
              markerEnd={`url(#${markerId})`}
            />
          ))}
          {layout.knoten.map(renderKnoten)}
        </svg>
      </div>
      {statuskette.length > 0 && (
        <div className="pz-kette">
          <span className="pz-kette-label">Statuskette</span>
          {statuskette.map((st, i) => (
            <React.Fragment key={`${st}-${i}`}>
              {i > 0 && <span className="pz-kette-pfeil">→</span>}
              <span className="pz-chip">{st}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      {authToken && (
        <div className="pz-laeufe">
          <div className="pz-laeufe-kopf">
            <span className="pz-kette-label">Läufe</span>
            <button type="button" className="k-btn k-btn--sm" onClick={ladeLaeufe}>
              Aktualisieren
            </button>
            {gewaehlt != null && (
              <button type="button" className="k-btn k-btn--sm" onClick={() => setGewaehlt(null)}>
                Auswahl aufheben
              </button>
            )}
          </div>
          {laeufeFehler && <div className="pz-leer" style={{ color: 'var(--red)' }}>{laeufeFehler}</div>}
          {laeufe.length === 0 && !laeufeFehler && <div className="pz-leer">Noch keine Läufe.</div>}
          {laeufe.length > 0 && (
            <table className="k-table">
              <thead>
                <tr>
                  <th>Objekt</th>
                  <th>Auslöser</th>
                  <th>Mitarbeiter</th>
                  <th>Stand</th>
                  <th>Dauer</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {laeufe.map(l => {
                  const aktiv = l.status === 'LAEUFT' || l.status === 'WARTET_FREIGABE';
                  return (
                    <tr
                      key={l.lauf_id}
                      className={`pz-lauf-zeile${gewaehlt === l.lauf_id ? ' is-gewaehlt' : ''}`}
                      onClick={() => setGewaehlt(gewaehlt === l.lauf_id ? null : l.lauf_id)}
                    >
                      <td>
                        <button
                          type="button"
                          className="pz-zeilen-knopf"
                          aria-pressed={gewaehlt === l.lauf_id}
                          onClick={e => {
                            e.stopPropagation();
                            setGewaehlt(gewaehlt === l.lauf_id ? null : l.lauf_id);
                          }}
                        >
                          {l.entitaet_typ ? `${l.entitaet_typ} #${l.entitaet_id ?? ''}` : `Lauf #${l.lauf_id}`}
                        </button>
                      </td>
                      <td>{AUSLOESER_TEXT[l.ausloeser_typ] ?? l.ausloeser_typ}</td>
                      <td>{l.mitarbeiter_name ?? '–'}</td>
                      <td>
                        <span className={`pz-chip pz-chip--${l.status.toLowerCase()}`}>
                          {STATUS_TEXT[l.status] ?? l.status}
                        </span>
                        {l.abbruch_grund && <span className="pz-grund">{l.abbruch_grund}</span>}
                      </td>
                      <td>{dauerText(l.dauer_s)}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {l.konversation_id != null && (
                          <button
                            type="button"
                            className="k-btn k-btn--sm"
                            onClick={e => {
                              e.stopPropagation();
                              setProtokollLauf(protokollLauf === l.lauf_id ? null : l.lauf_id);
                            }}
                          >
                            Protokoll
                          </button>
                        )}{' '}
                        {aktiv && (
                          <button
                            type="button"
                            className="k-btn k-btn--danger k-btn--sm"
                            onClick={e => {
                              e.stopPropagation();
                              abbrechen(l);
                            }}
                          >
                            Abbrechen
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {protokollLauf != null && (
            <LaufProtokoll
              authToken={authToken}
              konversationId={laeufe.find(l => l.lauf_id === protokollLauf)?.konversation_id ?? null}
              onClose={() => setProtokollLauf(null)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProzessAnsicht;
